import { api } from "./api-client.js";
import type { CurrentUser } from "./session.js";

// adminOnly 항목은 04-일반사용자-계정-추가-설계서.md §3-1에 따라 role=USER 사이드바에서 숨긴다.
// 계정·연동상태는 완전히 숨기지는 않고, 비밀번호 변경은 footer의 별도 링크로 role 무관하게 노출한다.
const NAV_ITEMS: Array<{ hash: string; label: string; adminOnly?: boolean }> = [
  { hash: "#/dashboard", label: "대시보드" },
  { hash: "#/performance", label: "수익률" },
  { hash: "#/trades", label: "거래내역" },
  { hash: "#/daily-summary", label: "일별 요약" },
  { hash: "#/stats", label: "통계" },
  { hash: "#/watchlist", label: "관리종목" },
  { hash: "#/users", label: "사용자 관리", adminOnly: true },
  { hash: "#/account", label: "계정·연동상태", adminOnly: true },
  { hash: "#/login-history", label: "로그인 이력", adminOnly: true },
];

// 002 §7: 화면5(설정변경)는 Phase2 — 이번 Phase1에는 네비게이션에 넣지 않는다.
//
// 모바일(좁은 화면)에서는 사이드바가 고정폭(220px)을 그대로 차지하면 본문이 심하게 눌려서
// 글자가 한 글자씩 줄바꿈되는 문제가 있었다(사용자가 실제 모바일 화면 스크린샷으로 지적함).
// 그래서 좁은 화면에서만 사이드바를 오프캔버스(기본 숨김) + 햄버거 토글로 바꾼다 — CSS 미디어
// 쿼리로 넓은 화면에서는 항상 보이게 유지, 좁은 화면에서만 .sidebar-open 클래스로 슬라이드인.
export function renderLayout(
  root: HTMLElement,
  activeHash: string,
  onLogout: () => void,
  user: CurrentUser | null,
): HTMLElement {
  root.innerHTML = "";
  const layout = document.createElement("div");
  layout.className = "layout";

  // "JaTem" 글자를 누르면 새로고침 — 배포 직후 반영 여부를 쉽게 재확인하고 싶다는 요청.
  // 서비스워커가 문서(index.html)는 network-first로 바뀌었으니 location.reload()만으로도
  // 캐시에 걸리지 않고 서버의 최신 상태를 다시 받아온다.
  function refreshApp(): void {
    window.location.reload();
  }

  const topbar = document.createElement("div");
  topbar.className = "mobile-topbar";
  const menuBtn = document.createElement("button");
  menuBtn.className = "menu-toggle";
  menuBtn.setAttribute("aria-label", "메뉴 열기");
  menuBtn.textContent = "☰";
  const topbarBrandWrap = document.createElement("div");
  topbarBrandWrap.className = "topbar-brand-wrap";
  const topbarBrand = document.createElement("button");
  topbarBrand.className = "mobile-topbar-brand";
  topbarBrand.textContent = "JaTem";
  topbarBrand.type = "button";
  topbarBrand.title = "새로고침";
  topbarBrand.addEventListener("click", refreshApp);
  topbarBrandWrap.appendChild(topbarBrand);
  if (user) {
    const topbarUserBadge = document.createElement("span");
    topbarUserBadge.className = "brand-user-badge";
    topbarUserBadge.textContent = user.username;
    topbarBrandWrap.appendChild(topbarUserBadge);
  }
  topbar.appendChild(menuBtn);
  topbar.appendChild(topbarBrandWrap);

  const backdrop = document.createElement("div");
  backdrop.className = "sidebar-backdrop";

  const sidebar = document.createElement("aside");
  sidebar.className = "sidebar";

  const brand = document.createElement("button");
  brand.className = "sidebar-brand";
  brand.type = "button";
  brand.title = "새로고침";
  const brandName = document.createElement("span");
  brandName.textContent = "JaTem";
  brand.appendChild(brandName);
  if (user) {
    const brandUserBadge = document.createElement("span");
    brandUserBadge.className = "brand-user-badge";
    brandUserBadge.textContent = user.username;
    brand.appendChild(brandUserBadge);
  }
  brand.addEventListener("click", refreshApp);
  sidebar.appendChild(brand);

  const nav = document.createElement("nav");
  nav.className = "sidebar-nav";
  for (const item of NAV_ITEMS) {
    if (item.adminOnly && user?.role !== "ADMIN") continue;
    const a = document.createElement("a");
    a.href = item.hash;
    a.textContent = item.label;
    if (item.hash === activeHash) a.classList.add("active");
    a.addEventListener("click", () => closeSidebar());
    nav.appendChild(a);
  }
  sidebar.appendChild(nav);

  const footer = document.createElement("div");
  footer.className = "sidebar-footer";

  // role 무관 노출 — 계정·연동상태 메뉴 자체는 USER에게 숨기지만(위 nav 필터), 비밀번호 변경만은
  // 예외로 허용한다(04-일반사용자-계정-추가-설계서.md §3-1). #/account 페이지가 role을 보고
  // USER에게는 비밀번호 변경 카드만 보여준다.
  const changePasswordBtn = document.createElement("button");
  changePasswordBtn.className = "btn-secondary";
  changePasswordBtn.textContent = "비밀번호 변경";
  changePasswordBtn.style.width = "100%";
  changePasswordBtn.style.marginBottom = "8px";
  changePasswordBtn.addEventListener("click", () => {
    closeSidebar();
    window.location.hash = "#/account";
  });
  footer.appendChild(changePasswordBtn);

  const logoutBtn = document.createElement("button");
  logoutBtn.className = "btn-secondary";
  logoutBtn.textContent = "로그아웃";
  logoutBtn.style.width = "100%";
  logoutBtn.addEventListener("click", async () => {
    try {
      await api.post("/api/auth/logout");
    } finally {
      onLogout();
    }
  });
  footer.appendChild(logoutBtn);
  sidebar.appendChild(footer);

  function openSidebar(): void {
    sidebar.classList.add("open");
    backdrop.classList.add("open");
  }
  function closeSidebar(): void {
    sidebar.classList.remove("open");
    backdrop.classList.remove("open");
  }
  menuBtn.addEventListener("click", () => {
    sidebar.classList.contains("open") ? closeSidebar() : openSidebar();
  });
  backdrop.addEventListener("click", () => closeSidebar());

  const content = document.createElement("main");
  content.className = "content";

  layout.appendChild(topbar);
  layout.appendChild(backdrop);
  layout.appendChild(sidebar);
  layout.appendChild(content);
  root.appendChild(layout);

  return content;
}
